import Editor, { useMonaco } from '@monaco-editor/react'
import { useEffect, useRef } from 'react'
import type * as monacoNS from 'monaco-editor'
import { Box } from '@chakra-ui/react'

export type BackendError = {
  message: string
  line?: number // 1-based
  column?: number // 1-based
}

type Props = {
  value: string
  onChange: (v: string) => void
  readOnly?: boolean
  placeholder?: string
  /**
   * Optional array of backend errors to show as red markers.
   * If only message is provided, it will be attached to line 1, col 1.
   */
  backendErrors?: BackendError[]
  height?: string
  /**
   * Called whenever Monaco reports any JS syntax/semantic errors in the model.
   * Use this to block form submit.
   */
  onSyntaxErrorChange?: (hasErrors: boolean) => void
}

const MODEL_LANGUAGE = 'javascript'
const BACKEND_OWNER = 'backend'

export default function CodeEditor({
  value,
  onChange,
  readOnly,
  placeholder,
  backendErrors = [],
  height = '260px',
  onSyntaxErrorChange,
}: Props) {
  const monaco = useMonaco()

  // Keep references for cleanup and marker lookups
  const modelRef = useRef<monacoNS.editor.ITextModel | null>(null)
  const disposablesRef = useRef<monacoNS.IDisposable[]>([])
  const editorRef = useRef<monacoNS.editor.IStandaloneCodeEditor | null>(null)

  // Create or reuse a dedicated model so we can manage markers reliably
  useEffect(() => {
    if (!monaco) return

    // Create a unique in-memory URI so multiple editors don't clash
    const uri = monaco.Uri.parse(
      `inmemory://model/${Math.random().toString(36).slice(2)}.js`,
    )

    const model =
      monaco.editor.getModel(uri) ??
      monaco.editor.createModel(value ?? '', MODEL_LANGUAGE, uri)

    modelRef.current = model

    // Initial backend markers install
    setBackendMarkers(monaco, model, backendErrors)

    // Subscribe to Monaco’s marker changes to surface syntax/semantic errors
    const sub = monaco.editor.onDidChangeMarkers(changed => {
      if (!modelRef.current || !onSyntaxErrorChange) return
      const { uri: currentUri } = modelRef.current
      if (!changed.some(u => u.path === currentUri.path)) return

      const markers = monaco.editor.getModelMarkers({ resource: currentUri })
      const hasErr = markers.some(
        m => m.severity === monaco.MarkerSeverity.Error,
      )
      onSyntaxErrorChange(hasErr)
    })
    disposablesRef.current.push(sub)

    // Emit once for initial content
    if (onSyntaxErrorChange) {
      const initialMarkers = monaco.editor.getModelMarkers({
        resource: model.uri,
      })
      const hasErr = initialMarkers.some(
        m => m.severity === monaco.MarkerSeverity.Error,
      )
      onSyntaxErrorChange(hasErr)
    }

    return () => {
      disposablesRef.current.forEach(d => d.dispose())
      disposablesRef.current = []
      if (modelRef.current) {
        const stillUsed = monaco.editor
          .getEditors()
          .some(e => e.getModel() === modelRef.current)
        if (!stillUsed) modelRef.current.dispose()
      }
      modelRef.current = null
    }
  }, [monaco, value])

  useEffect(() => {
    if (!monaco || !modelRef.current) return
    setBackendMarkers(monaco, modelRef.current, backendErrors)
  }, [monaco, backendErrors])

  return (
    <Box borderRadius="md" overflow="hidden" mt="1.5em">
      <Editor
        height={height}
        defaultLanguage={MODEL_LANGUAGE}
        value={value}
        onChange={v => onChange(v || '')}
        theme="vs-dark"
        options={{
          readOnly: !!readOnly,
          placeholder,
          fontSize: 14,
          minimap: { enabled: false },
          wordWrap: 'on',
          automaticLayout: true,
          scrollBeyondLastLine: false,
          tabSize: 4,
          quickSuggestions: false,
        }}
        onMount={editor => {
          editorRef.current = editor
          if (modelRef.current) editor.setModel(modelRef.current)
        }}
      />
    </Box>
  )
}

function setBackendMarkers(
  monaco: typeof monacoNS,
  model: monacoNS.editor.ITextModel,
  errors: BackendError[],
) {
  monaco.editor.setModelMarkers(
    model,
    BACKEND_OWNER,
    (errors || []).map(e => ({
      startLineNumber: e.line ?? 1,
      startColumn: e.column ?? 1,
      endLineNumber: e.line ?? 1,
      endColumn: (e.column ?? 1) + 1,
      message: e.message,
      severity: monaco.MarkerSeverity.Error,
    })),
  )
}
